import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AppContext } from './app-context';
import { HttpStatusCode } from './http-constants';
import { NodeRequest, NodeRequestOptions } from './node-connection';

/**
 * PowerShell Language mode values.
 * https://learn.microsoft.com/en-us/dotnet/api/system.management.automation.pslanguagemode?view=powershellsdk-7.0.0
 */
export enum PSLanguageMode {
    FullLanguage = 0,
    RestrictedLanguage = 1,
    NoLanguage = 2,
    ConstrainedLanguage = 3
}

/**
 * Represents the data returned from the psLanguageMode endpoint - /api/nodes/<node>/features/wdac/operations/psLanguageMode
 */
export interface PsLanguageModeResult {
    /**
     * Status of the request/response.
     */
    status: HttpStatusCode;

    /**
     * PowerShell Language Mode of the target node.
     */
    psLanguageMode: PSLanguageMode;
}

/**
 * Windows Defender Application Control (WDAC) Constants.
 */
export class WdacConstants {
    /**
     * PowerShell Language mode API endpoint.
     */
    public static psLanguageModeUrl = 'features/wdac/operations/psLanguageMode';

    /**
     * Wdac cache reset API endpoint.
     */
    public static resetWdacCacheUrl = 'features/wdac/operations/resetWdacCache';
}

/**
 * Windows Defender Application Control (WDAC) operations class.
 */
export class WdacOperations {
    /**
     * Initializes a new instance of the WdacOperations class.
     *
     * @param appContext the application context.
     */
    constructor(private appContext: AppContext) { }

    /**
     * Gets the PowerShell Language Mode of a specified targetNodeName.
     * @param targetNodeName the machine name to get the WdacMode for.
     * @param options the NodeRequestOptions for this API call.
     */
    public getPsLanguageMode(targetNodeName: string, options?: NodeRequestOptions): Observable<PsLanguageModeResult> {
        return this.appContext.node.get(targetNodeName, WdacConstants.psLanguageModeUrl, <NodeRequest>options)
            .pipe(
                catchError((error, caught) => {
                    // catch elevation error and retry after elevated.
                    if (this.appContext.gateway.isElevationRequired(error)) {
                        return this.appContext.gateway.pollingGatewayElevated()
                            .pipe(switchMap(elevated => elevated ? caught : throwError(() => error)));
                    }
                    return throwError(() => error);
                })
            );
    }

    /**
     * Resets the WDAC cache of a specified targetNodeName.
     * @param targetNodeName the machine name to reset the WDAC cache for.
     * @param options the NodeRequestOptions for this API call.
     */
    public resetWdacCache(targetNodeName: string, options?: NodeRequestOptions): Observable<void> {
        return this.appContext.node.post(targetNodeName, WdacConstants.resetWdacCacheUrl, null, <NodeRequest>options)
            .pipe(map(() => null));
    }
}
